// src/pages/Notificacoes.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationCard from '../components/NotificationCard';

import {
  getNotificacoes,
  marcarComoVisualizada, // legado
} from '../services/notificacoesService';

import { aceitarSociedade, rejeitarSociedade } from '../services/sociosService';
import {
  aceitarParceria,
  rejeitarParceria,
} from '../services/parceriasService';

// ✅ Auth
import { useAuth } from '../context/AuthContext';

// ✅ Novo (preferido p/ marcar como lida)
import { markNotificacaoAsRead } from '../services/negociosService';

const TABS = [
  { key: 'todas', label: 'Todas' },
  { key: 'pendentes', label: 'Pendentes' },
  { key: 'lidas', label: 'Lidas' },
];

function isActionableType(tipo) {
  // compatibilidade com legado "conexao"
  return ['sociedade', 'parceria', 'conexao'].includes(tipo);
}

function normalizeTipoParceria(tipo) {
  return tipo === 'conexao' ? 'parceria' : tipo;
}

// aceita string, number, Date, ou Firestore Timestamp-like { seconds }
function parseDate(anyDate) {
  if (!anyDate) return new Date(0);
  if (typeof anyDate?.seconds === 'number')
    return new Date(anyDate.seconds * 1000);
  if (typeof anyDate === 'string' || typeof anyDate === 'number')
    return new Date(anyDate);
  if (anyDate instanceof Date) return anyDate;
  return new Date(0);
}

// fallback de avatar por tipo, caso não exista foto do autor
function avatarFor(tipo) {
  switch (tipo) {
    case 'sociedade':
      return '/imagens/avatars/avatar-sociedade.jpg';
    case 'parceria':
      return '/imagens/avatars/avatar-conexao.jpg';
    case 'novo-imovel':
    case 'venda-imovel':
    case 'valor-atualizado':
      return '/imagens/avatars/avatar-imovel.jpg';
    case 'negocio_match':
      return '/imagens/avatars/avatar-imovel.jpg';
    default:
      return '/imagens/avatars/avatar-padrao.jpg';
  }
}

export default function Notificacoes() {
  const navigate = useNavigate();
  const { usuario } = useAuth();

  const [notificacoes, setNotificacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('todas');
  const [q, setQ] = useState('');
  const [busyIds, setBusyIds] = useState({}); // loading por item
  const [bulkBusy, setBulkBusy] = useState(false); // loading global p/ "Marcar todas como lidas"

  const fetchData = async () => {
    try {
      setLoading(true);
      setErr('');
      const data = await getNotificacoes();

      // normaliza + ordena por data (desc)
      const norm = (data || []).map((n) => ({
        ...n,

        // normaliza tipos legado
        tipo: normalizeTipoParceria(n?.tipo),

        // compat: backend novo pode usar createdAt/updatedAt; legado usa data
        _date: parseDate(n?.data || n?.createdAt || n?.updatedAt),

        // campos compatíveis com backend novo (actor*) e antigo (usuarioOrigem)
        actorNome: n?.actorNome || n?.usuarioOrigem?.nome || null,
        actorFotoUrl: n?.actorFotoUrl || n?.usuarioOrigem?.foto || null,

        // compat read: novo usa read; legado usa visualizado
        visualizado:
          typeof n?.visualizado === 'boolean'
            ? n.visualizado
            : typeof n?.read === 'boolean'
              ? n.read
              : false,

        // compat mensagem/title
        mensagem: n?.mensagem || n?.title || '',
      }));

      norm.sort((a, b) => b._date - a._date);
      setNotificacoes(norm);
    } catch (e) {
      console.error(e);
      setErr('Não foi possível carregar suas notificações.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const counts = useMemo(() => {
    const total = notificacoes.length;
    const pend = notificacoes.filter(
      (n) => !n.visualizado && isActionableType(n.tipo) && n.refId
    ).length;
    const lidas = notificacoes.filter((n) => n.visualizado).length;
    const naoLidas = total - lidas;
    return { total, pend, lidas, naoLidas };
  }, [notificacoes]);

  const filtered = useMemo(() => {
    let arr = [...notificacoes];

    if (tab === 'pendentes') {
      arr = arr.filter(
        (n) => !n.visualizado && isActionableType(n.tipo) && n.refId
      );
    } else if (tab === 'lidas') {
      arr = arr.filter((n) => n.visualizado);
    }

    if (q?.trim()) {
      const qq = q.trim().toLowerCase();
      arr = arr.filter(
        (n) =>
          (n.mensagem || '').toLowerCase().includes(qq) ||
          (n.tipo || '').toLowerCase().includes(qq) ||
          (n.actorNome || '').toLowerCase().includes(qq)
      );
    }

    return arr;
  }, [notificacoes, tab, q]);

  // ✅ marca como lida (preferindo novo backend)
  const marcarLida = async (id) => {
    try {
      setBusyIds((s) => ({ ...s, [id]: true }));

      if (usuario?.uid) {
        try {
          await markNotificacaoAsRead(usuario.uid, id);
        } catch (_) {
          await marcarComoVisualizada(id);
        }
      } else {
        await marcarComoVisualizada(id);
      }

      setNotificacoes((prev) =>
        prev.map((n) => (n.id === id ? { ...n, visualizado: true } : n))
      );
    } catch (e) {
      console.error(e);
    } finally {
      setBusyIds((s) => ({ ...s, [id]: false }));
    }
  };

  const marcarTodasComoLidas = async () => {
    const ids = notificacoes.filter((n) => !n.visualizado).map((n) => n.id);
    if (!ids.length) return;

    try {
      setBulkBusy(true);

      if (usuario?.uid) {
        await Promise.all(
          ids.map(async (id) => {
            try {
              await markNotificacaoAsRead(usuario.uid, id);
            } catch (_) {
              await marcarComoVisualizada(id);
            }
          })
        );
      } else {
        await Promise.all(ids.map((id) => marcarComoVisualizada(id)));
      }

      setNotificacoes((prev) => prev.map((n) => ({ ...n, visualizado: true })));
    } catch (e) {
      console.error(e);
    } finally {
      setBulkBusy(false);
    }
  };

  const handleAceitar = async (item) => {
    if (!item?.refId) return;
    try {
      setBusyIds((s) => ({ ...s, [item.id]: true }));

      if (item.tipo === 'sociedade') await aceitarSociedade(item.refId);
      else if (item.tipo === 'parceria') await aceitarParceria(item.refId);

      await marcarLida(item.id);
    } catch (err) {
      console.error('Erro ao aceitar:', err);
    } finally {
      setBusyIds((s) => ({ ...s, [item.id]: false }));
    }
  };

  const handleRejeitar = async (item) => {
    if (!item?.refId) return;
    try {
      setBusyIds((s) => ({ ...s, [item.id]: true }));

      if (item.tipo === 'sociedade') await rejeitarSociedade(item.refId);
      else if (item.tipo === 'parceria') await rejeitarParceria(item.refId);

      await marcarLida(item.id);
    } catch (err) {
      console.error('Erro ao rejeitar:', err);
    } finally {
      setBusyIds((s) => ({ ...s, [item.id]: false }));
    }
  };

  // mapeia link para eventos
  const computeLink = (item) => {
    if (!item) return undefined;

    // ✅ NEGÓCIO MATCH: navega para o NegocioPage e já abre matches
    if (item.tipo === 'negocio_match' && item.negocioId) {
      return `/negocios/${item.negocioId}?openMatches=1`;
    }

    // imóveis
    const tiposImovel = ['novo-imovel', 'venda-imovel', 'valor-atualizado'];
    if (tiposImovel.includes(item.tipo) && item.imovelId) {
      return `/imovel/${item.imovelId}`;
    }

    return undefined;
  };

  // ✅ Clique do card (para negocio_match, garantimos marcar lida + navegar)
  const handleClickNotification = async (item) => {
    if (!item) return;

    if (item.tipo === 'negocio_match' && item.negocioId) {
      // marca lida antes de navegar
      if (!item.visualizado) {
        await marcarLida(item.id);
      }

      navigate(`/negocios/${item.negocioId}?openMatches=1`, {
        state: { fromNotifId: item.id, openMatches: true },
      });
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Notificações</h1>
          {counts.naoLidas > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 ring-1 ring-blue-200">
              {counts.naoLidas} não lida{counts.naoLidas > 1 ? 's' : ''}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Buscar por texto, tipo ou autor..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button
            onClick={fetchData}
            className="px-3 py-2 text-sm bg-white border rounded hover:bg-gray-50"
          >
            Recarregar
          </button>
          <button
            onClick={marcarTodasComoLidas}
            disabled={counts.naoLidas === 0 || bulkBusy}
            className="px-3 py-2 text-sm bg-blue-600 text-white rounded disabled:opacity-60 hover:bg-blue-700"
            title={
              counts.naoLidas === 0
                ? 'Nenhuma não lida'
                : 'Marcar todas como lidas'
            }
          >
            {bulkBusy ? 'Processando...' : 'Marcar todas como lidas'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {TABS.map((t) => {
          const count =
            t.key === 'todas'
              ? counts.total
              : t.key === 'pendentes'
                ? counts.pend
                : counts.lidas;

          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded text-sm border flex items-center gap-2 ${
                tab === t.key
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <span>{t.label}</span>
              <span
                className={`min-w-[1.5rem] text-center rounded-full text-xs px-1 ${
                  tab === t.key ? 'bg-white/20' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Status de erro / loading / vazio */}
      {err && (
        <div className="p-3 mb-3 rounded bg-red-50 text-red-700 text-sm border border-red-200">
          {err}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="h-20 animate-pulse bg-gray-100 rounded border"
            />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-gray-500">Nenhuma notificação para mostrar.</p>
      ) : (
        <ul className="space-y-2">
          {filtered.map((item) => {
            const foto =
              item.actorFotoUrl ||
              item?.usuarioOrigem?.foto ||
              avatarFor(item.tipo);

            const link = computeLink(item);

            return (
              <li key={item.id}>
                <div
                  onClick={() => {
                    // Só intercepta clique para negocio_match (opção 02)
                    if (item.tipo === 'negocio_match')
                      handleClickNotification(item);
                  }}
                >
                  <NotificationCard
                    tipo={item.tipo}
                    mensagem={item.mensagem}
                    time={item._date}
                    read={!!item.visualizado}
                    isPending={
                      !item.visualizado &&
                      isActionableType(item.tipo) &&
                      !!item.refId
                    }
                    onAccept={() => handleAceitar(item)}
                    onReject={() => handleRejeitar(item)}
                    onMarkRead={() => marcarLida(item.id)}
                    loading={!!busyIds[item.id]}
                    link={link}
                    avatarUrl={foto}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
